import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartCard } from '@/components/shared/chart-card';
import { FundSelect } from '@/components/shared/fund-select';
import { SimpleBarChart } from '@/components/shared/simple-bar-chart';
import { api } from '@/lib/api';
import { formatCompactMoney, formatMoney, formatTrendDate } from '@/utils/format';

type TrendGrain = 'day' | 'week' | 'month';

export type GivingTrendPoint = {
  label: string;
  value: number;
  grain?: TrendGrain;
};

type InsightsTrend = {
  trend: Array<{
    label: string;
    amountMinor: number;
    grain?: TrendGrain;
  }>;
};

const toChartRows = (points: GivingTrendPoint[]) =>
  points.map((point) => ({
    label: formatTrendDate(point.label, point.grain),
    value: point.value,
  }));

export const GivingTrendChart = ({
  title,
  emptyTitle,
  to,
  dateFrom,
  dateTo,
  baseCurrency,
  fallbackPoints,
  fallbackLoading = false,
  contributionType,
  programKind,
  height = 200,
  className,
  plotFixed = false,
}: {
  title: string;
  emptyTitle: string;
  to?: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  baseCurrency: string;
  fallbackPoints?: GivingTrendPoint[];
  fallbackLoading?: boolean;
  contributionType?: string;
  programKind?: 'service' | 'program';
  height?: number;
  className?: string;
  plotFixed?: boolean;
}) => {
  const { t } = useTranslation('giving');
  const [fundId, setFundId] = useState<string | null>(null);

  const filtered = useQuery({
    queryKey: [
      'giving',
      'insights',
      'trend',
      dateFrom,
      dateTo,
      fundId,
      contributionType,
      programKind,
    ],
    queryFn: () =>
      api.get<InsightsTrend>('/giving/insights', {
        query: {
          dateFrom: dateFrom ?? undefined,
          dateTo: dateTo ?? undefined,
          fundId: fundId ?? undefined,
          contributionType,
          programKind,
        },
      }),
    enabled: Boolean(fundId),
  });

  const points: GivingTrendPoint[] = fundId
    ? (filtered.data?.trend ?? []).map((row) => ({
        label: row.label,
        value: row.amountMinor,
        grain: row.grain,
      }))
    : (fallbackPoints ?? []);

  const loading = fundId ? filtered.isPending : fallbackLoading;
  const empty = fundId ? t('trendChart.emptyFund') : emptyTitle;

  return (
    <ChartCard
      title={title}
      to={to}
      loading={loading}
      hasData={points.length > 0}
      empty={{ title: empty }}
      height={height}
      className={className}
      plotFixed={plotFixed}
      actions={
        <FundSelect
          value={fundId}
          onChange={setFundId}
          allOptionLabel={t('trendChart.allFunds')}
          allowCreate={false}
          size="sm"
          className="w-48"
          aria-label={t('trendChart.fund')}
        />
      }
    >
      <SimpleBarChart
        data={toChartRows(points)}
        formatValue={(value) => formatMoney(value, baseCurrency, { showSymbol: true })}
        formatTick={(value) => formatCompactMoney(value, baseCurrency)}
        height={Math.max(height - 20, 120)}
      />
    </ChartCard>
  );
};
